import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { canInvite } from "../../invite-permissions.js";
import { buildPdfFilename } from "../../pdf-filename.js";
import { findUniqueProjectByCode } from "../../project-context.js";
import { AppShell } from "../components/AppShell";
import { getFunctionUrl } from "../config";
import { api } from "../lib/api";
import { getAuthToken } from "../lib/auth";
import type { AccessContext, ReportSummary } from "../types/api";

const AMS11_PROJECT_CODE = "ams11";
const DAGRAPPORT_GPT_URL =
  "https://chatgpt.com/g/g-6a8b3b73bdb48191a6165d4eff945be5-ams11-productie";

const STATUS_LABEL: Record<string, string> = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
};

function formatDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function HomePage() {
  const navigate = useNavigate();
  const [context, setContext] = useState<AccessContext | null>(null);
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectCode, setProjectCode] = useState(AMS11_PROJECT_CODE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const accessContext = await api.getAccessContext();
        if (controller.signal.aborted) return;
        const project = findUniqueProjectByCode(
          accessContext,
          AMS11_PROJECT_CODE,
        );
        if (!project?.project_id) {
          throw new Error(
            "AMS11 is niet eenduidig beschikbaar voor deze gebruiker.",
          );
        }
        const recentReports = await api.listReports(project.project_id, 20);
        if (controller.signal.aborted) return;
        setContext(accessContext);
        setProjectId(project.project_id);
        setProjectCode(project.project_code ?? AMS11_PROJECT_CODE);
        setReports(recentReports ?? []);
      } catch (cause) {
        if (!controller.signal.aborted) {
          setError(cause instanceof Error ? cause.message : "Onbekende fout");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  const mayInvite = useMemo(
    () => Boolean(context && projectId && canInvite(context, projectId)),
    [
      context,
      projectId,
    ],
  );

  async function downloadPdf(report: ReportSummary) {
    try {
      setDownloadingId(report.day_report_id);
      const token = await getAuthToken();
      if (!token) throw new Error("Niet ingelogd");
      const response = await fetch(
        getFunctionUrl("dbvdw-pdf", `?day_report_id=${report.day_report_id}`),
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        throw new Error(
          `PDF kon niet worden gegenereerd (HTTP ${response.status}).`,
        );
      }
      const url = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildPdfFilename({
        projectCode,
        reportDate: report.report_date,
        revision: report.revision,
      });
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      globalThis.alert(
        `Fout bij downloaden PDF: ${cause instanceof Error ? cause.message : "Onbekende fout"}`,
      );
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <AppShell>
      <main className="panel">
        <h1>Dagverslagen</h1>
        {loading && (
          <section>
            <p>Verslagen laden...</p>
          </section>
        )}
        {error && (
          <section>
            <p className="error">Fout: {error}</p>
          </section>
        )}
        {!loading && !error && (
          <section>
            <div className="actions top-actions">
              <a
                className="btn"
                href={DAGRAPPORT_GPT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Nieuw verslag
              </a>
              {mayInvite && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => navigate("/invite/")}
                >
                  Gebruiker uitnodigen
                </button>
              )}
            </div>

            <div className="reports-list">
              {reports.length === 0
                ? (
                  <div
                    className="report-card"
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <p className="muted">
                      Nog geen verslagen. Klik “Nieuw verslag” om te beginnen.
                    </p>
                  </div>
                )
                : reports.map((report) => (
                  <div
                    className="report-card"
                    data-id={report.day_report_id}
                    key={report.day_report_id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/reports/?id=${report.day_report_id}`)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        navigate(`/reports/?id=${report.day_report_id}`);
                      }
                    }}
                  >
                    <div className="report-card-info">
                      <h3>{formatDate(report.report_date)}</h3>
                      <div className="report-meta">
                        <span
                          className={`report-status status-${report.status}`}
                        >
                          {STATUS_LABEL[report.status] ?? report.status}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="download-pdf-card-btn secondary"
                      disabled={downloadingId === report.day_report_id}
                      onClick={(event) => {
                        event.stopPropagation();
                        void downloadPdf(report);
                      }}
                    >
                      📄 PDF
                    </button>
                  </div>
                ))}
            </div>
          </section>
        )}
      </main>
    </AppShell>
  );
}
